import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, Eye, EyeOff, Monitor, LogOut } from "lucide-react";
import { toast } from "sonner";

function SessionsPanel() {
  const [rows, setRows] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sessions");
      setRows(r.data.sessions || []);
      setCurrentSid(r.data.current_sid || null);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const revokeOthers = async () => {
    if (!confirm("Sign out every other device? You'll stay signed in here.")) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/sessions/revoke-others", {});
      toast.success(`Signed out ${r.data.revoked || 0} other device(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
    setBusy(false);
  };

  const revokeOne = async (sid) => {
    setBusy(true);
    try {
      await api.post(`/auth/sessions/${sid}/revoke`, {});
      toast.success("Device signed out");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    setBusy(false);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const others = rows.filter(r => !r.is_current);

  return (
    <div className="border-t pt-3 mt-3 space-y-2" data-testid="sessions-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          Signed-in devices · {rows.length}
        </div>
        {others.length > 0 && (
          <Button type="button" size="sm" variant="outline" onClick={revokeOthers} disabled={busy}
            className="h-7 text-[11px] rounded-sm" data-testid="revoke-others-btn">
            <LogOut className="w-3 h-3 mr-1" /> Sign out other devices
          </Button>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading sessions…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground">No active sessions.</div>
      ) : (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
          {rows.map(s => (
            <li key={s.sid} className={`text-[11px] p-2 rounded-sm border flex items-center gap-2 ${s.is_current ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}
              data-testid={`session-row-${s.sid?.slice(0, 8)}`}>
              <Monitor className={`w-3.5 h-3.5 shrink-0 ${s.is_current ? "text-emerald-500" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">
                  {s.user_agent || "Unknown device"}
                  {s.is_current && <span className="ml-1.5 text-[9px] font-mono uppercase text-emerald-500">· this device</span>}
                </div>
                <div className="text-muted-foreground truncate">
                  {s.ip || "unknown ip"} · last seen {fmtDate(s.last_seen_at)}
                </div>
              </div>
              {!s.is_current && (
                <button type="button" onClick={() => revokeOne(s.sid)} disabled={busy}
                  className="text-[10px] text-red-500 hover:underline shrink-0"
                  data-testid={`revoke-session-${s.sid?.slice(0, 8)}`}>
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Universal password-change dialog. Works for every logged-in user
 *  (regular, company owner, agency admin, super admin) via /api/auth/change-password.
 *  Pass a custom `endpoint` (e.g. "/admin/change-password") to override for
 *  contexts that need the legacy super-admin route. */
export default function ChangePasswordDialog({ open, onOpenChange, endpoint = "/auth/change-password" }) {
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState({ min_length: 8 });

  useEffect(() => {
    if (open) api.get("/auth/password-policy").then(r => setPolicy(r.data || {})).catch(() => {});
  }, [open]);

  const reset = () => { setCur(""); setNp(""); setNp2(""); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (np !== np2) return toast.error("New passwords don't match");
    if (np.length < (policy.min_length || 8)) return toast.error(`New password must be at least ${policy.min_length || 8} characters`);
    if (cur === np) return toast.error("New password must be different from current password");
    setBusy(true);
    try {
      await api.post(endpoint, { current_password: cur, new_password: np });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  const rules = [
    { pass: np.length >= (policy.min_length || 8), label: `At least ${policy.min_length || 8} characters` },
    ...(policy.require_upper ? [{ pass: /[A-Z]/.test(np), label: "One uppercase letter" }] : []),
    ...(policy.require_lower ? [{ pass: /[a-z]/.test(np), label: "One lowercase letter" }] : []),
    ...(policy.require_number ? [{ pass: /\d/.test(np), label: "One number" }] : []),
    ...(policy.require_symbol ? [{ pass: /[^A-Za-z0-9]/.test(np), label: "One symbol" }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-sm max-w-md max-h-[92vh] overflow-y-auto" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-accent" /> Change password
          </DialogTitle>
          <DialogDescription>
            Use a strong password. The rules below are enforced by your organization.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Current password</Label>
            <div className="relative mt-1">
              <Input type={show ? "text" : "password"} required value={cur}
                onChange={(e) => setCur(e.target.value)} autoFocus
                data-testid="cpw-current" className="pr-9" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Toggle password visibility">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>New password</Label>
            <Input type={show ? "text" : "password"} required minLength={policy.min_length || 8}
              value={np} onChange={(e) => setNp(e.target.value)} className="mt-1" data-testid="cpw-new" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type={show ? "text" : "password"} required minLength={policy.min_length || 8}
              value={np2} onChange={(e) => setNp2(e.target.value)} className="mt-1" data-testid="cpw-confirm" />
          </div>
          {rules.length > 0 && (
            <ul className="text-[11px] space-y-0.5 pt-1" data-testid="cpw-rules">
              {rules.map((r, i) => (
                <li key={i} className={r.pass ? "text-emerald-500" : "text-muted-foreground"}>
                  {r.pass ? "✓" : "•"} {r.label}
                </li>
              ))}
              {policy.no_reuse_count > 0 && (
                <li className="text-muted-foreground">• Cannot reuse your last {policy.no_reuse_count} passwords</li>
              )}
            </ul>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              data-testid="cpw-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="cpw-submit">
              <Lock className="w-4 h-4 mr-1.5" /> {busy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
          <div className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Passwords are hashed with bcrypt — nothing is stored plain-text.
          </div>
        </form>
        {open && <SessionsPanel />}
      </DialogContent>
    </Dialog>
  );
}
